import _ from 'lodash';

/**
 * Safely parses JSON with a fallback for invalid JSON
 * @param {string} jsonString - JSON string to parse
 * @param {any} defaultValue - Default value if parsing fails
 * @returns {any} Parsed JSON or default value
 */
const safeJsonParse = (jsonString, defaultValue = []) => {
  if (!jsonString) return defaultValue;
  try {
    return JSON.parse(jsonString);
  } catch (error) {
    console.warn('JSON parse error:', error);
    return defaultValue;
  }
};

/**
 * Generates a new invoice number based on the current date and version
 * @param {number} version - Current version number
 * @returns {string} Formatted invoice number
 */
export const generateInvoiceNumber = (version = 1) => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const randomNum = String(Math.floor(Math.random() * 9999)).padStart(4, '0');

  // Format: RFM-YYYYMM-XXXX
  const baseNumber = `RFM-${year}${month}-${randomNum}`;

  // Add version suffix if it's not version 1
  return version > 1 ? `${baseNumber}-V${version}` : baseNumber;
};

/**
 * Retrieves the next version number for an invoice
 * @param {Object} pb - PocketBase instance
 * @param {string} originalInvoiceId - ID of the original invoice
 * @returns {Promise<number>} Next version number
 */
export const getNextVersionNumber = async (pb, originalInvoiceId) => {
  try {
    const result = await pb.collection('invoices').getList(1, 1, {
      filter: `original_invoice = "${originalInvoiceId}"`,
      sort: '-version',
    });

    const highestVersion = result.items.length > 0 ? result.items[0].version : 0;
    return highestVersion + 1;
  } catch (error) {
    console.error('Error getting next version number:', error);
    throw error;
  }
};

/**
 * Creates a version history entry with error handling
 * @param {Object} currentData - Current invoice data
 * @param {Object} previousData - Previous version data
 * @returns {Object} Version history entry
 */
export const createVersionHistoryEntry = (currentData, previousData) => {
  const changes = [];
  const fieldsToTrack = [
    'title',
    'products',
    'notes',
    'total_amount',
    'currency',
    'customer_additional_notes'
  ];

  for (const field of fieldsToTrack) {
    const currentValue = currentData[field];
    const previousValue = previousData?.[field];

    // Skip if both values are undefined or null
    if (currentValue === undefined && previousValue === undefined) continue;
    if (currentValue === null && previousValue === null) continue;

    // Handle JSON string comparisons
    if (typeof currentValue === 'string' && typeof previousValue === 'string') {
      try {
        const currentJson = safeJsonParse(currentValue);
        const previousJson = safeJsonParse(previousValue);
        if (!_.isEqual(currentJson, previousJson)) {
          changes.push({
            field,
            previousValue: previousValue,
            newValue: currentValue,
            timestamp: new Date().toISOString()
          });
        }
      } catch {
        // If JSON parsing fails, compare as strings
        if (currentValue !== previousValue) {
          changes.push({
            field,
            previousValue: previousValue,
            newValue: currentValue,
            timestamp: new Date().toISOString()
          });
        }
      }
    } else if (!_.isEqual(currentValue, previousValue)) {
      changes.push({
        field,
        previousValue: previousValue,
        newValue: currentValue,
        timestamp: new Date().toISOString()
      });
    }
  }

  return {
    version: currentData.version,
    changes,
    timestamp: new Date().toISOString(),
    user: currentData.user || 'system'
  };
};



/**
 * Retrieves version history for an invoice with improved error handling
 * @param {Object} pb - PocketBase instance
 * @param {string} originalInvoiceId - ID of the original invoice
 * @returns {Promise<Array>} Version history
 */
// versionHandler.js içindeki getVersionHistory fonksiyonunu güncelleyelim

export const getVersionHistory = async (pb, originalInvoiceId) => {
  try {
    if (!originalInvoiceId) {
      console.warn('No originalInvoiceId provided');
      return [];
    }

    const filter = `original_invoice = "${originalInvoiceId}" || id = "${originalInvoiceId}"`;

    const result = await pb.collection('invoices').getList(1, 100, {
      filter: filter,
      sort: '-version',
      expand: 'customer'
    });

    return result.items.map(version => {
      // Version history'yi güvenli bir şekilde parse edelim
      let parsedHistory = [];
      if (version.version_history) {
        try {
          parsedHistory = typeof version.version_history === 'string' 
            ? JSON.parse(version.version_history) 
            : version.version_history;
        } catch (error) {
          console.warn('Version history parse error:', error);
          parsedHistory = [];
        }
      }

      return {
        id: version.id,
        version: version.version || 1,
        invoice_number: version.invoice_number,
        status: version.status,
        version_note: version.version_note || '-',
        created: version.created,
        // Tüm olası tarih alanlarını ekleyelim
        exchange_rate_date: version.exchange_rate_date,
        invoice_date: version.invoice_date,
        due_date: version.due_date,
        valid_until: version.valid_until,
        // Ek bilgiler
        customer: version.expand?.customer,
        total_amount: version.total_amount,
        currency: version.currency,
        history: parsedHistory
      };
    });
  } catch (error) {
    console.error('Error getting version history:', error);
    return [];
  }
};

// Versiyon oluşturma fonksiyonunu da güncelleyelim
export const createNewVersion = async (pb, currentInvoice, versionNote) => {
  try {
    const originalId = currentInvoice.original_invoice || currentInvoice.id;
    const nextVersion = await getNextVersionNumber(pb, originalId);
    const newInvoiceNumber = generateInvoiceNumber(nextVersion);

    // Önceki versiyonun verisini alalım
    const previousVersion = await pb.collection('invoices').getOne(currentInvoice.id);

    // Version history'yi güvenli bir şekilde parse edelim
    let existingHistory = [];
    try {
      existingHistory = typeof previousVersion.version_history === 'string'
        ? JSON.parse(previousVersion.version_history)
        : previousVersion.version_history || [];
    } catch (error) {
      console.warn('Previous version history parse error:', error);
      existingHistory = [];
    }

    // Yeni history entry oluşturalım
    const newHistoryEntry = createVersionHistoryEntry(currentInvoice, previousVersion);

    // Yeni versiyon datası hazırlayalım
    const newVersionData = {
      ..._.omit(currentInvoice, ['id', 'created', 'updated']),
      invoice_number: newInvoiceNumber,
      version: nextVersion,
      original_invoice: originalId,
      version_note: versionNote,
      status: 'Taslak',
      version_history: JSON.stringify([...existingHistory, newHistoryEntry])
    };

    // Yeni versiyonu oluşturalım
    const createdVersion = await pb.collection('invoices').create(newVersionData);
    return createdVersion;
  } catch (error) {
    console.error('Error creating new version:', error);
    throw error;
  }
};

/**
 * Initializes a new invoice with version tracking
 * @param {Object} invoiceData - Initial invoice data
 * @returns {Object} Initialized invoice data
 */
export const initializeInvoice = (invoiceData) => {
  return {
    ...invoiceData,
    version: 1,
    invoice_number: generateInvoiceNumber(1),
    version_history: JSON.stringify([]),
    status: 'Taslak',
    original_invoice: null
  };
};

/**
 * Validates if a new version can be created
 * @param {Object} currentInvoice - Current invoice data
 * @returns {Object} Validation result
 */
export const validateVersionCreation = (currentInvoice) => {
  const invalidStatuses = ['İptal Edildi', 'Silindi'];

  if (!currentInvoice) {
    return {
      valid: false,
      message: 'Geçerli bir teklif bulunamadı.'
    };
  }

  if (invalidStatuses.includes(currentInvoice.status)) {
    return {
      valid: false,
      message: 'İptal edilmiş veya silinmiş teklifler için yeni versiyon oluşturulamaz.'
    };
  }

  return { valid: true };
};